import {AxiosPromise} from 'axios';
import api from './api';
import apiGraphQL from './apiGraphQL';
import {
  Coach,
  Device,
  Organization,
  Profile,
  ShopifySubscription,
  Surface,
  Team,
  Venue,
} from '../interfaces';

function postAdminUtil(params: {
  job: string;
  data: any;
  onError?: () => void;
  onSuccess?: () => void;
}) {
  const {job, data, onError, onSuccess} = params;

  return new Promise<AxiosPromise>((resolve, reject) => {
    try {
      api
        .post(`/admin/util`, {job, data})
        .then((response: any) => {
          resolve(response);
          if (onSuccess) {
            onSuccess();
          }
        })
        .catch((e: Error) => {
          reject(e);
          if (onError) {
            onError();
          }
        });
    } catch (e) {
      reject(e);
      if (onError) {
        onError();
      }
    }
  });
}

const getOrganizations = () => {
  return new Promise<Organization[]>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `{
            adminOrganizations {
              id
              name
              country
              stateOrProvince
            }
          }`,
          variables: {},
        })
        .then(response => {
          resolve(response.data.data.adminOrganizations);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const getOrganization = (id: number) => {
  return new Promise<Organization>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `query ($id: Int!) {
            adminOrganization(id: $id) {
              id
              name
              country
              stateOrProvince
              hideLeaderboard
              teams {
                id
                name
                teamSessionGroupsEnabled
                isPro
                players {
                  profile {
                    id
                    email
                    firstName
                    lastName
                    birthYear
                    gender
                    role
                    membershipLevel
                    membershipTrialEndDate
                    lastSessionDate
                  }
                }
              }
              profiles {
                id
                email
                firstName
                lastName
                birthYear
                gender
                role
                membershipLevel
                membershipTrialEndDate
                lastSessionDate
              }
            }
          }`,
          variables: {
            id,
          },
        })
        .then(response => {
          resolve(response.data.data.adminOrganization);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const createOrganization = (input: {
  name: string;
  country: string;
  stateOrProvince: string;
}) => {
  return new Promise<Team>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation CreateOrganization($input: CreateOrganizationInput!) {
            adminCreateOrganization(input: $input) {
              id,
              name,
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminCreateOrganization);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const updateOrganization = (input: {
  id: number;
  name: string;
  country: string;
  stateOrProvince: string;
  hideLeaderboard: boolean;
}) => {
  return new Promise<Organization>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation UpdateOrganization($input: UpdateOrganizationInput!) {
            adminUpdateOrganization(input: $input) {
              id,
              name,
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminUpdateOrganization);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const getTeams = () => {
  return new Promise<Team[]>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `{
            adminTeams {
              id
              name
              code
              teamSessionGroupsEnabled
              isPro
              membershipTrialEndDate
            }
          }`,
          variables: {},
        })
        .then(response => {
          resolve(response.data.data.adminTeams);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const getTeam = (id: number) => {
  return new Promise<Team>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `query ($id: Int!) {
          adminTeam(id: $id) {
              id
              name
              code
              teamSessionGroupsEnabled
              isPro
              hideLeaderboard
              membershipTrialEndDate
              organization {
                id
                name
              }
              players {
                id
                number
                isActive
                profile {
                  id
                  email
                  firstName
                  lastName
                  position
                  devices {
                    id
                  }
                }
              }
              coaches {
                id
                profile {
                  id
                  email
                  firstName
                  lastName
                }
              }
            }
          }`,
          variables: {
            id,
          },
        })
        .then(response => {
          resolve(response.data.data.adminTeam);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const createTeam = (input: {
  name: string;
  code?: string;
  organizationId: number;
  teamSessionGroupsEnabled: boolean;
  isPro: boolean;
}) => {
  return new Promise<Team>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation CreateTeam($input: CreateTeamInput!) {
            adminCreateTeam(input: $input) {
              id,
              name,
              code,
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminCreateTeam);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const updateTeam = (input: {
  id: number;
  name: string;
  code?: string;
  organizationId: number;
  teamSessionGroupsEnabled: boolean;
  isPro: boolean;
  hideLeaderboard: boolean;
  membershipTrialEndDate: string | null;
}) => {
  return new Promise<Team>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation UpdateTeam($input: UpdateTeamInput!) {
            adminUpdateTeam(input: $input) {
              id,
              name,
              code,
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminUpdateTeam);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const createCoach = (input: {email: string; teamId: number}) => {
  return new Promise<Coach>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation CreateCoach($input: CreateCoachInput!) {
            adminCreateCoach(input: $input) {
              id,
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminCreateCoach);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const deleteCoach = (input: {id: number}) => {
  return new Promise<Coach>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation DeleteCoach($input: DeleteCoachInput!) {
            adminDeleteCoach(input: $input) {
              id,
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminDeleteCoach);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const getProfiles = () => {
  return new Promise<Profile[]>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `{
            adminProfileDeleteRequests {
              id
              email
              deletedAt
              firstName
              lastName
            }
          }`,
          variables: {},
        })
        .then(response => {
          resolve(response.data.data.adminProfileDeleteRequests);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const deleteProfile = (input: {id: number}) => {
  return new Promise<{success: boolean; error: string}>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation DeleteProfile($input: DeleteProfileInput!) {
            adminDeleteProfile(input: $input) {
              success,
              error
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminDeleteProfile);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const deleteAccount = (profileImpersonateId: number) => {
  return new Promise<void>((resolve, reject) => {
    try {
      api
        .post('/account/delete', {profileImpersonateId})
        .then(() => {
          resolve();
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const restoreProfile = (profileId: number) => {
  return new Promise<{success: boolean; error: string}>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation RestoreProfile($profileId: Int!) {
            adminReenableProfile(profileId: $profileId) {
              success,
              error
            }
          }`,
          variables: {
            profileId,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminReenableProfile);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const getShopifySubscriptionPendingReturns = () => {
  return new Promise<ShopifySubscription[]>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `{
            adminShopifySubscriptionPendingReturns {
              id
              email
              quantity
              returnOrderName
              returnStatus
            }
          }`,
          variables: {},
        })
        .then(response => {
          resolve(response.data.data.adminShopifySubscriptionPendingReturns);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const completeShopifySubscriptionReturn = (input: {
  shopifySubscriptionId: number;
}) => {
  return new Promise<{success: boolean; error: string}>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation AdminShopifySubscriptionCompleteReturnInput($input: AdminShopifySubscriptionCompleteReturnInput!) {
            adminShopifySubscriptionCompleteReturn(input: $input) {
              success,
              error
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminShopifySubscriptionCompleteReturn);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const getShopifySubscriptions = (searchText?: string) => {
  return new Promise<ShopifySubscription[]>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `query ($searchText: String) {
            adminShopifySubscriptions(searchText: $searchText) {
              id
              email
              quantity
              currency
              isClaimed
              orderIntervalUnit
              orderIntervalFrequency
              createdAt
              deliveredAt
              canceledAt
              expiringAt
              expiredAt
              pauseStartDate
              pauseEndDate
              returnOrderName
              returnStatus
              isMonthly
              plan
              status
            }
          }`,
          variables: {
            searchText,
          },
        })
        .then(response => {
          resolve(response.data.data.adminShopifySubscriptions);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const getAdminProfiles = (searchText?: string) => {
  return new Promise<Profile[]>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `query ($searchText: String) {
            adminProfiles(searchText: $searchText) {
              id
              firstName
              lastName
              email
              role
              membershipLevel
              membershipTrialEndDate
              lastSessionDate
            }
          }`,
          variables: {
            searchText,
          },
        })
        .then(response => {
          resolve(response.data.data.adminProfiles);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const getAdminProfile = (profileId: number) => {
  return new Promise<Profile>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `query ($profileId: Int!) {
            adminProfile(profileId: $profileId) {
              id
              firstName
              lastName
              email
              role
              gender
              position
              shot
              dateOfBirth
              teamName
              country
              stateOrProvince
              membershipLevel
              membershipTrialEndDate
              lastSessionDate
              pendingParentalConsent
              deletedAt
              shopifySubscription {
                id
                status
                plan
              }
              parentFamily {
                id
                parentProfile {
                  id
                  firstName
                  lastName
                  email
                  role
                }
              }
              childFamilies {
                id
                parentProfile {
                  id
                  firstName
                  lastName
                  email
                  role
                }
                childProfiles {
                  id
                  firstName
                  lastName
                  email
                  role
                }
              }
            }
          }`,
          variables: {
            profileId,
          },
        })
        .then(response => {
          resolve(response.data.data.adminProfile);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const editProfile = (input: {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  dateOfBirth?: string | null;
}) => {
  return new Promise<Profile>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation AdminProfileEdit($input: AdminProfileEditInput!) {
            adminProfileEdit(input: $input) {
              id
              firstName
              lastName
              dateOfBirth
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminProfileEdit);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const adminCreateCognitoUserForProfile = (input: {
  profileId: number;
  email: string;
  tempPassword: string;
}) => {
  return new Promise<{success: boolean; error: string}>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation AdminCreateCognitoUserForProfile($input: AdminCreateCognitoUserForProfileInput!) {
            adminCreateCognitoUserForProfile(input: $input) {
              success,
              error
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminCreateCognitoUserForProfile);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const getShopifySubscription = (id: number) => {
  return new Promise<ShopifySubscription>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `query ($id: Int!) {
            adminShopifySubscription(id: $id) {
              id
              email
              quantity
              currency
              rechargeSubscriptionId
              orderIntervalUnit
              orderIntervalFrequency
              createdAt
              deliveredAt
              canceledAt
              expiringAt
              expiredAt
              pauseStartDate
              pauseEndDate
              rechargeSubscriptionData {
                intervalFrequency
                intervalUnit
                quantity
                nextChargeScheduledAt
              }
              returnOrderName
              returnStatus
              isMonthly
              plan
              status
              profiles {
                id
                email
                firstName
                lastName
                role
                membershipLevel
                membershipTrialEndDate
                lastSessionDate
              }
            }
          }`,
          variables: {
            id,
          },
        })
        .then(response => {
          resolve(response.data.data.adminShopifySubscription);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const updateShopifySubscription = (input: {
  id: number;
  expiringAt?: string | null;
}) => {
  return new Promise<ShopifySubscription>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation AdminUpdateShopifySubscription($input: AdminUpdateShopifySubscriptionInput!) {
            adminUpdateShopifySubscription(input: $input) {
              id
              expiringAt
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminUpdateShopifySubscription);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const searchDevice = (input: {qrCode?: string; deviceId?: number}) => {
  return new Promise<Device>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `query ($input: AdminSearchDeviceInput!) {
            adminSearchDevice(input: $input) {
              id
              name
              type
              identifier
              hrMonitorType
              hrMonitorIdentifier
              qrCode
              appFwVersion
              commsConfigLabel
              is12MonthMembership
              hasStartedShopifySubscription
              profile {
                id
                email
                firstName
                lastName
                players {
                  id
                  isActive
                  team {
                    id
                    name
                  }
                }
              }
            }
          }`,
          variables: {
            input: {
              qrCode: input.qrCode,
              deviceId: input.deviceId,
            },
          },
        })
        .then(response => {
          if (response.data.errors) {
            reject(
              new Error(
                response.data.errors
                  .map((e: {message: string}) => e.message)
                  .join('\n'),
              ),
            );
          } else {
            resolve(response.data.data.adminSearchDevice);
          }
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const unpairDevice = (input: {
  deviceId: number;
  resetTrialStarted?: boolean;
}) => {
  return new Promise<{success: boolean; error: string}>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation AdminUnpairDevice($input: AdminUnpairDeviceInput!) {
            adminUnpairDevice(input: $input) {
              success,
              error
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            reject(
              new Error(
                response.data.errors
                  .map((e: {message: string}) => e.message)
                  .join('\n'),
              ),
            );
          } else {
            resolve(response.data.data.adminUnpairDevice);
          }
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const pairDevice = (input: {deviceId: number; profileId: number}) => {
  return new Promise<{success: boolean; error: string}>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation AdminPairDevice($input: AdminPairDeviceInput!) {
            adminPairDevice(input: $input) {
              success,
              error
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            reject(
              new Error(
                response.data.errors
                  .map((e: {message: string}) => e.message)
                  .join('\n'),
              ),
            );
          } else {
            resolve(response.data.data.adminPairDevice);
          }
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const getVenues = () => {
  return new Promise<Venue[]>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `{
            adminVenues {
              id
              name
              country
              stateOrProvince
              address
              city
              lat
              lon
              surfaces {
                id
                name
              }
            }
          }`,
          variables: {},
        })
        .then(response => {
          resolve(response.data.data.adminVenues);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const getVenue = (id: number) => {
  return new Promise<Venue>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `query ($id: Int!) {
            adminVenue(id: $id) {
              id
              name
              country
              stateOrProvince
              address
              city
              lat
              lon
              surfaces {
                id
                name
                livebarnStreamName
              }
            }
          }`,
          variables: {
            id,
          },
        })
        .then(response => {
          resolve(response.data.data.adminVenue);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const createVenue = (input: {
  name: string;
  country: string;
  stateOrProvince: string;
  address?: string;
  city?: string;
  lat?: number;
  lon?: number;
}) => {
  return new Promise<Venue>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation CreateVenue($input: CreateVenueInput!) {
            adminCreateVenue(input: $input) {
              id,
              name,
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminCreateVenue);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const updateVenue = (input: {
  id: number;
  name?: string;
  country?: string;
  stateOrProvince?: string;
  address?: string;
  city?: string;
  lat?: number;
  lon?: number;
}) => {
  return new Promise<Venue>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation UpdateVenue($input: UpdateVenueInput!) {
            adminUpdateVenue(input: $input) {
              id,
              name,
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminUpdateVenue);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const deleteVenue = (input: {id: number}) => {
  return new Promise<{success: boolean; error: string}>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation DeleteVenue($input: DeleteVenueInput!) {
            adminDeleteVenue(input: $input) {
              success,
              error
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminDeleteVenue);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const createSurface = (input: {
  name: string;
  venueId: number;
  livebarnStreamName?: string;
}) => {
  return new Promise<Surface>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation CreateSurface($input: CreateSurfaceInput!) {
            adminCreateSurface(input: $input) {
              id,
              name,
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminCreateSurface);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const updateSurface = (input: {
  id: number;
  name?: string;
  livebarnStreamName?: string;
}) => {
  return new Promise<Surface>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation UpdateSurface($input: UpdateSurfaceInput!) {
            adminUpdateSurface(input: $input) {
              id,
              name,
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminUpdateSurface);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

const deleteSurface = (input: {id: number}) => {
  return new Promise<{success: boolean; error: string}>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation DeleteSurface($input: DeleteSurfaceInput!) {
            adminDeleteSurface(input: $input) {
              success,
              error
            }
          }`,
          variables: {
            input,
          },
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response.data.data.adminDeleteSurface);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};

export {
  postAdminUtil,
  getOrganizations,
  getOrganization,
  createOrganization,
  getTeams,
  getTeam,
  createTeam,
  createCoach,
  deleteCoach,
  updateOrganization,
  updateTeam,
  getProfiles,
  deleteProfile,
  deleteAccount,
  restoreProfile,
  getShopifySubscriptionPendingReturns,
  completeShopifySubscriptionReturn,
  getShopifySubscriptions,
  getShopifySubscription,
  updateShopifySubscription,
  getAdminProfiles,
  getAdminProfile,
  editProfile,
  adminCreateCognitoUserForProfile,
  searchDevice,
  pairDevice,
  unpairDevice,
  getVenues,
  getVenue,
  createVenue,
  updateVenue,
  deleteVenue,
  createSurface,
  updateSurface,
  deleteSurface,
};
